import axios from 'axios';
import { useEffect, useState } from 'react';
import {
    Button,
    Form,
    Modal,
    Spinner,
    Table
} from 'react-bootstrap';
import { FaEdit, FaPlus } from 'react-icons/fa';
import Layout from '../../../components/Layouts/Layouts/Layout';
import DashboardHeader from '../../../components/basic/DashboardHeader';
import { formatDateAndTime } from '../../../components/basic/TimeAndDate';
import { getURLbyEndPointV2 } from '../../../store/api';

const CollaborationPortal = () => {
    const [countries, setCountries] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedCountry, setSelectedCountry] = useState(null);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [error, setError] = useState('');
    const [formData, setFormData] = useState({
        country: '',
        companyName: '',
        heading: '',
        description: '',
        logo: null,
        banner: null,
    });
    const [formErrors, setFormErrors] = useState({});
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        fetchCountries();
    }, []);

    const fetchCountries = async () => {
        try {
            setLoading(true);
            setError('');
            const response = await axios.get(getURLbyEndPointV2('getAllCollaborators'));

            // const formattedData = response.data.data.flatMap((country) =>
            //     country.details.map((detail) => ({
            //         _id: detail._id,
            //         country: country.country,
            //         companyName: detail.companyName || 'N/A',
            //         heading: detail.heading || 'N/A',
            //         description: detail.description || 'N/A',
            //         logoUrl: detail.logoUrl || country.logoUrl || null,
            //         bannerUrl: detail.bannerUrl || null,
            //         createdAt: detail.createdAt,
            //     }))
            // );

            const formattedData = response.data.data
            setCountries(formattedData);
        } catch (error) {
            console.error('Error fetching countries:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleEditClick = (company) => {
        setSelectedCountry(company);
        setFormData({
            companyName: company.companyName,
            country: company.country,
            heading: company.heading,
            description: company.description,
            logo: null,
            banner: null,
        });
        setShowModal(true);
    };

    const handleAddClick = () => {
        setSelectedCountry(null);
        setFormData({
            country: '',
            companyName: '',
            heading: '',
            description: '',
            logo: null,
            banner: null,
        });
        setShowModal(true);
    };

    const validateForm = () => {
        const errors = {};
        if (!formData.country) errors.country = 'Country is required';
        if (!formData.companyName) errors.companyName = 'Company Name is required';
        if (!formData.heading) errors.heading = 'Heading is required';
        if (!formData.description) errors.description = 'Description is required';
        if (!formData.logo && !selectedCountry) errors.logo = 'Logo is required';
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
        setFormErrors({ ...formErrors, [name]: '' });
    };

    const handleFileChange = (e) => {
        const { name, files } = e.target;
        if (files[0]) {
            setFormData({ ...formData, [name]: files[0] });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;
    
        try {
            setSubmitLoading(true);
            setError('');
    
            const formDataToSend = new FormData();
            formDataToSend.append('country', formData.country);
            formDataToSend.append('companyName', formData.companyName);
            formDataToSend.append('heading', formData.heading);
            formDataToSend.append('description', formData.description);
    
            // Ensure files are correctly appended
            if (formData.logo) formDataToSend.append('logo', formData.logo);
            if (formData.banner) formDataToSend.append('banner', formData.banner);
    
            if (selectedCountry) {
                await axios.put(
                    getURLbyEndPointV2('updateParticipateCountry') + selectedCountry._id,
                    formDataToSend,
                    { headers: { 'Content-Type': 'multipart/form-data' } }
                );
            } else {
                await axios.post(
                    getURLbyEndPointV2('createCollaborator'),
                    formDataToSend,
                    { headers: { 'Content-Type': 'multipart/form-data' } }
                );
            }
    
            setShowModal(false);
            fetchCountries();
        } catch (error) {
            setError('Failed to submit form. Please try again.');
            console.error('Error submitting form:', error);
        } finally {
            setSubmitLoading(false);
        }
    };

    const filteredCountries = countries.filter((country) =>
        country.country.toLowerCase().includes(searchTerm.toLowerCase()) ||
        country.companyName.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <Layout>
            <div className="d-flex flex-column justify-content-center align-items-center px-3">
                <div className="main-width d-flex flex-column">
                    <hr />
                    <DashboardHeader heading="COLLABORATION PORTAL" backUrl="/master-collaboration-portal" />
                    <hr />

                    <div className="d-flex justify-content-between mb-3">
                        <Button variant="primary" onClick={handleAddClick}>
                            <FaPlus /> Add Entry
                        </Button>
                        <input
                            className="form-control w-25"
                            type="search"
                            placeholder="Search by company name or country"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>

                    <Table responsive striped bordered hover>
                        <thead>
                            <tr>
                                <th>Sr No.</th>
                                <th>Country</th>
                                <th>Company Name</th>
                                <th>Heading</th>
                                <th>Description</th>
                                <th>Logo</th>
                                <th>Banner</th>
                                <th>Created At</th>
                                <th>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan={9} className="text-center">
                                        <Spinner animation="border" />
                                    </td>
                                </tr>
                            ) : filteredCountries.length > 0 ? (
                                filteredCountries.map((company, index) => (
                                    <tr key={company._id}>
                                        <td>{index + 1}</td>
                                        <td>{company.country}</td>
                                        <td>{company.companyName}</td>
                                        <td>{company.heading}</td>
                                        <td style={{ whiteSpace: 'pre-wrap' }}>{company.description}</td>
                                        <td>
                                            {company.logoUrl && (
                                                <img
                                                    src={company.logoUrl}
                                                    alt="Logo"
                                                    style={{ width: '50px', height: '50px', objectFit: 'contain' }}
                                                />
                                            )}
                                        </td>
                                        <td>
                                            {company.bannerUrl && (
                                                <img
                                                    src={company.bannerUrl}
                                                    alt="Banner"
                                                    style={{ width: '100px', height: '50px', objectFit: 'cover' }}
                                                />
                                            )}
                                        </td>
                                        <td>{formatDateAndTime(company.createdAt)}</td>
                                        <td>
                                            <Button
                                                size="sm"
                                                variant="warning"
                                                onClick={() => handleEditClick(company)}
                                            >
                                                <FaEdit />
                                            </Button>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={9} className="text-center">
                                        No Matching Data
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>

                    <Modal size="xl" show={showModal} onHide={() => setShowModal(false)} centered>
                        <Modal.Header closeButton>
                            <Modal.Title>{selectedCountry ? 'Edit' : 'Add'} Collaborator</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            {error && <div className="alert alert-danger">{error}</div>}
                            <Form onSubmit={handleSubmit}>
                                <div className="row">
                                    <div className="col-md-6">
                                        <Form.Group className="mb-3">
                                            <Form.Label>Country</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="country"
                                                value={formData.country}
                                                onChange={handleInputChange}
                                                isInvalid={!!formErrors.country}
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {formErrors.country}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </div>

                                    <div className="col-md-6">
                                        <Form.Group className="mb-3">
                                            <Form.Label>Company Name</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="companyName"
                                                value={formData.companyName}
                                                onChange={handleInputChange}
                                                isInvalid={!!formErrors.companyName}
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {formErrors.companyName}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </div>
                                </div>

                                <Form.Group className="mb-3">
                                    <Form.Label>Heading</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="heading"
                                        value={formData.heading}
                                        onChange={handleInputChange}
                                        isInvalid={!!formErrors.heading}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {formErrors.heading}
                                    </Form.Control.Feedback>
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>Description</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={5}
                                        name="description"
                                        value={formData.description}
                                        onChange={handleInputChange}
                                        isInvalid={!!formErrors.description}
                                        style={{ whiteSpace: 'pre-wrap' }}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {formErrors.description}
                                    </Form.Control.Feedback>
                                </Form.Group>

                                <div className="row">
                                    <div className="col-md-6">
                                        <Form.Group className="mb-3">
                                            <Form.Label>Logo</Form.Label>
                                            <Form.Control
                                                type="file"
                                                name="logo"
                                                onChange={handleFileChange}
                                                isInvalid={!!formErrors.logo}
                                                accept="image/*"
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {formErrors.logo}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </div>

                                    <div className="col-md-6">
                                        <Form.Group className="mb-3">
                                            <Form.Label>Banner</Form.Label>
                                            <Form.Control
                                                type="file"
                                                name="banner"
                                                onChange={handleFileChange}
                                                accept="image/*"
                                            />
                                        </Form.Group>
                                    </div>
                                </div>

                                <div className="text-center">
                                    <Button type="submit" disabled={submitLoading}>
                                        {submitLoading ? (
                                            <>
                                                <Spinner size="sm" className="me-2" />
                                                {selectedCountry ? 'Updating...' : 'Adding...'}
                                            </>
                                        ) : (
                                            selectedCountry ? 'Update' : 'Add'
                                        )}
                                    </Button>
                                </div>
                            </Form>
                        </Modal.Body>
                    </Modal>
                </div>
            </div>
        </Layout>
    );
};

export default CollaborationPortal;